import { useId, useState } from 'react';

import type { LegalDocumentApi } from '@/modules/legal/types';
import { SignupFlowShell } from '@/modules/signup-flow/components/layout/SignupFlowShell';
import { Button } from '@/modules/ui/components/button/Button';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';

const TERMS_LINK_TEXT = 'Terms & Conditions';

interface AcceptTermsScreenProps {
  terms: LegalDocumentApi;
  isAccepting: boolean;
  error?: string | null;
  isLoggingOut?: boolean;
  onAccept: () => void;
  onLogout: () => void;
}

/**
 * The Terms Gate: a required, unticked checkbox the user must tick before
 * anything else in the workspace opens. The statement comes from the API so
 * it always matches the version the acceptance is recorded against.
 */
export function AcceptTermsScreen({
  terms,
  isAccepting,
  error,
  isLoggingOut,
  onAccept,
  onLogout,
}: AcceptTermsScreenProps) {
  const [isChecked, setIsChecked] = useState(false);
  const checkboxId = useId();

  return (
    <SignupFlowShell
      headerRight={
        <button
          type="button"
          disabled={isLoggingOut}
          onClick={onLogout}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 rounded-md px-2 py-1 text-[13px] font-medium transition-colors outline-none focus-visible:ring-[3px] disabled:opacity-50"
        >
          Log out
        </button>
      }
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
            Before you continue
          </h1>
          <p className="text-muted-foreground text-[13px] leading-[19px]">
            Please confirm you accept our terms on behalf of your organisation.
          </p>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            autoFocus
            checked={isChecked}
            className="mt-0.5"
            id={checkboxId}
            onCheckedChange={(checked) => setIsChecked(checked === true)}
          />
          <label className="text-[14px] leading-5" htmlFor={checkboxId}>
            <TermsStatement terms={terms} />
          </label>
        </div>

        <div className="flex flex-col gap-3">
          {error && <InlineError>{error}</InlineError>}
          <Button
            className="self-start"
            disabled={!isChecked}
            loading={isAccepting}
            onClick={onAccept}
          >
            Continue
          </Button>
        </div>
      </div>
    </SignupFlowShell>
  );
}

/** The API statement with "Terms & Conditions" linked to the current terms page. */
function TermsStatement({ terms }: { terms: LegalDocumentApi }) {
  const text = terms.text ?? '';
  const linkStart = text.indexOf(TERMS_LINK_TEXT);
  if (!terms.url || linkStart < 0) return text;

  return (
    <>
      {text.slice(0, linkStart)}
      <a
        className="text-primary-700 underline underline-offset-2"
        href={terms.url}
        rel="noopener noreferrer"
        target="_blank"
      >
        {TERMS_LINK_TEXT}
      </a>
      {text.slice(linkStart + TERMS_LINK_TEXT.length)}
    </>
  );
}
